/**
 * تولیدکننده سهمیه‌های خاص (لبنیات، روغن، قند، آجیل)
 * کپی شده از new_front/src/lib/utils/quotaGenerator.js
 */
import { customRound, customRound2, customRoundFull, categorizeFraction } from './roundingUtils';

/**
 * تولید سهمیه لبنیات
 * @param {number} quotaCal1 - کالری سهمیه لبنیات
 * @param {Array} formattedData - داده‌های فرمت شده منوها
 * @param {string} dietOrder - نوع رژیم (increase یا decrease)
 * @returns {string|null} - متن سهمیه یا null
 */
export const generateDairyQuota = (quotaCal1, formattedData, dietOrder) => {
  try {
    if (!quotaCal1 || quotaCal1 <= 0) {
      return "سهمیه لبنیات ندارید";
    }

    if (!formattedData || formattedData.length === 0) {
      console.warn("داده‌های سهمیه‌ها خالی هستند.");
      return "سهمیه لبنیات ندارید";
    }

    // انتخاب نوع شیر بر اساس نوع رژیم
    const dairyType = dietOrder === 'increase' ? "شیر کامل (3 درصد)" : "شیر کم چرب (1.5 درصد)";
    
    // فیلتر سهمیه‌های لبنیات
    const dairyQuotas = formattedData.filter(
      (menu) => menu.form_type === "portion" && 
                menu.portion_type === "سهمیه لبنیات" && 
                menu.Breakfast_Name === dairyType && 
                menu.items.length > 0
    );

    if (dairyQuotas.length === 0) {
      console.error("هیچ سهمیه لبنیات معتبری یافت نشد.");
      return "سهمیه لبنیات ندارید";
    }

    // انتخاب تصادفی یک سهمیه
    const dairyRandomIndex = Math.floor(Math.random() * dairyQuotas.length);
    const foodDairyQuota = dairyQuotas[dairyRandomIndex];

    // پردازش آیتم‌ها
    const dairyValues = foodDairyQuota.items.map((item) => {
      const percentage = item.percentage / 100;
      const calories = item.calories;
      const targetCalories = percentage * quotaCal1;
      
      let result;
      switch (item.rounding_method) {
        case "round_half":
          result = customRound(targetCalories / calories);
          break;
        case "round_5g":
          result = customRound2(targetCalories / calories);
          break;
        case "round_quarter":
          result = categorizeFraction(targetCalories / calories);
          break;
        case "round_full":
          result = customRoundFull(targetCalories / calories);
          break;
        default:
          result = Math.round(targetCalories / calories);
          result = Math.max(result, 1);
      }
      return result;
    });

    // تولید متن سهمیه
    let resultDairyquota = foodDairyQuota.text || "";
    const dairyDollarSigns = (resultDairyquota.match(/\$/g) || []).length;
    if (dairyDollarSigns !== dairyValues.length) {
      console.warn(
        `تعداد $ (${dairyDollarSigns}) با تعداد آیتم‌ها (${dairyValues.length}) مطابقت ندارد`
      );
    }
    dairyValues.forEach((value) => {
      resultDairyquota = resultDairyquota.replace(/\$/, value);
    });

    return resultDairyquota;
  } catch (error) {
    console.error("خطا در تولید سهمیه لبنیات:", error);
    return "سهمیه لبنیات ندارید";
  }
};

/**
 * تولید سهمیه روغن زیتون
 * @param {number} quotaCal2 - کالری سهمیه روغن
 * @param {Array} formattedData - داده‌های فرمت شده منوها
 * @returns {string|null} - متن سهمیه یا null
 */
export const generateOliveOilQuota = (quotaCal2, formattedData) => {
  try {
    if (!quotaCal2 || quotaCal2 <= 0) {
      return 'سهمیه روغن ندارید';
    }

    if (!formattedData || formattedData.length === 0) {
      console.warn("داده‌های سهمیه‌ها خالی هستند.");
      return 'سهمیه روغن ندارید';
    }

    // فیلتر سهمیه‌های روغن زیتون
    const oliveOilQuotas = formattedData.filter(
      (menu) => menu.form_type === "portion" && 
                menu.portion_type === "سهمیه روغن زیتون" && 
                menu.Breakfast_Name === "روغن زیتون" && 
                menu.items.length > 0
    );

    if (oliveOilQuotas.length === 0) {
      console.error("هیچ سهمیه روغن زیتون معتبری یافت نشد.");
      return 'سهمیه روغن ندارید';
    }

    // انتخاب تصادفی یک سهمیه
    const oliveOilRandomIndex = Math.floor(Math.random() * oliveOilQuotas.length);
    const foodOliveOilQuota = oliveOilQuotas[oliveOilRandomIndex];

    // پردازش آیتم‌ها
    const oliveOilValues = foodOliveOilQuota.items.map((item) => {
      const percentage = item.percentage / 100;
      const calories = item.calories;
      const targetCalories = percentage * quotaCal2;
      
      let result;
      switch (item.rounding_method) {
        case "round_half":
          result = customRound(targetCalories / calories);
          break;
        case "round_5g":
          result = customRound2(targetCalories / calories);
          break;
        case "round_quarter":
          result = categorizeFraction(targetCalories / calories);
          break;
        case "round_full":
          result = customRoundFull(targetCalories / calories);
          break;
        default:
          result = Math.round(targetCalories / calories);
          result = Math.max(result, 1);
      }
      return result;
    });

    // تولید متن سهمیه
    let resultOliveOilSubstitute = foodOliveOilQuota.text || "";
    const oliveOilDollarSigns = (resultOliveOilSubstitute.match(/\$/g) || []).length;
    if (oliveOilDollarSigns !== oliveOilValues.length) {
      console.warn(
        `تعداد $ (${oliveOilDollarSigns}) با تعداد آیتم‌ها (${oliveOilValues.length}) مطابقت ندارد`
      );
    }
    oliveOilValues.forEach((value) => {
      resultOliveOilSubstitute = resultOliveOilSubstitute.replace(/\$/, value);
    });

    return resultOliveOilSubstitute;
  } catch (error) {
    console.error("خطا در تولید سهمیه روغن زیتون:", error);
    return 'سهمیه روغن ندارید';
  }
};

/**
 * تولید سهمیه قند یا آجیل
 * @param {number} quotaCal3 - کالری سهمیه قند/آجیل
 * @param {Array} formattedData - داده‌های فرمت شده منوها
 * @param {string} dietOrder - نوع رژیم (increase یا decrease)
 * @returns {Object|null} - object شامل text و type یا null
 */
export const generateSugarOrNutsQuota = (quotaCal3, formattedData, dietOrder) => {
  try {
    if (!quotaCal3 || quotaCal3 <= 0) {
      return {
        text: dietOrder === 'increase' ? 'سهمیه آجیل ندارید' : 'سهمیه قند ندارید',
        type: dietOrder === 'increase' ? 'nuts' : 'sugar'
      };
    }

    if (!formattedData || formattedData.length === 0) {
      console.warn("داده‌های سهمیه‌ها خالی هستند.");
      return {
        text: dietOrder === 'increase' ? 'سهمیه آجیل ندارید' : 'سهمیه قند ندارید',
        type: dietOrder === 'increase' ? 'nuts' : 'sugar'
      };
    }

    if (dietOrder === 'increase') {
      // فیلتر سهمیه‌های آجیل برای رژیم افزایش وزن
      const nutsQuotas = formattedData.filter(
        (menu) => menu.form_type === "portion" && 
                  menu.portion_type === "سهمیه آجیل" && 
                  menu.Breakfast_Name === "مغز" && 
                  menu.items.length > 0
      );

      if (nutsQuotas.length === 0) {
        console.error("هیچ سهمیه آجیل معتبری یافت نشد.");
        return {
          text: 'سهمیه آجیل ندارید',
          type: 'nuts'
        };
      }

      // انتخاب تصادفی یک سهمیه
      const nutsRandomIndex = Math.floor(Math.random() * nutsQuotas.length);
      const foodNutsQuota = nutsQuotas[nutsRandomIndex];

      // پردازش آیتم‌ها
      const nutsValues = foodNutsQuota.items.map((item) => {
        const percentage = item.percentage / 100;
        const calories = item.calories;
        const targetCalories = percentage * quotaCal3;
        
        let result;
        switch (item.rounding_method) {
          case "round_half":
            result = customRound(targetCalories / calories);
            break;
          case "round_5g":
            result = customRound2(targetCalories / calories);
            break;
          case "round_quarter":
            result = categorizeFraction(targetCalories / calories);
            break;
          case "round_full":
            result = customRoundFull(targetCalories / calories);
            break;
          default:
            result = categorizeFraction(targetCalories / calories);
            if (typeof result === 'number') {
              result = Math.max(result, 0.1);
            }
        }
        return result;
      });

      // تولید متن سهمیه
      let resultNutsSubstitute = foodNutsQuota.text || "";
      const nutsDollarSigns = (resultNutsSubstitute.match(/\$/g) || []).length;
      if (nutsDollarSigns !== nutsValues.length) {
        console.warn(
          `تعداد $ (${nutsDollarSigns}) با تعداد آیتم‌ها (${nutsValues.length}) مطابقت ندارد`
        );
      }
      nutsValues.forEach((value) => {
        resultNutsSubstitute = resultNutsSubstitute.replace(/\$/, value);
      });

      return {
        text: resultNutsSubstitute,
        type: 'nuts'
      };
    } else {
      // فیلتر سهمیه‌های قند برای رژیم کاهش وزن
      const sugarQuotas = formattedData.filter(
        (menu) => menu.form_type === "portion" && 
                  menu.portion_type === "سهمیه قند" && 
                  menu.Breakfast_Name === "حبه قند" && 
                  menu.items.length > 0
      );

      if (sugarQuotas.length === 0) {
        console.error("هیچ سهمیه قند معتبری یافت نشد.");
        return {
          text: 'سهمیه قند ندارید',
          type: 'sugar'
        };
      }

      // انتخاب تصادفی یک سهمیه
      const sugarRandomIndex = Math.floor(Math.random() * sugarQuotas.length);
      const foodSugarQuota = sugarQuotas[sugarRandomIndex];

      // پردازش آیتم‌ها
      const sugarValues = foodSugarQuota.items.map((item) => {
        const percentage = item.percentage / 100;
        const calories = item.calories;
        const targetCalories = percentage * quotaCal3;
        
        let result;
        switch (item.rounding_method) {
          case "round_half":
            result = customRound(targetCalories / calories);
            break;
          case "round_5g":
            result = customRound2(targetCalories / calories);
            break;
          case "round_quarter":
            result = categorizeFraction(targetCalories / calories);
            break;
          case "round_full":
            result = customRoundFull(targetCalories / calories);
            break;
          default:
            result = Math.round(targetCalories / calories);
            result = Math.max(result, 1);
        }
        return result;
      });

      // تولید متن سهمیه
      let resultSugarSubstitute = foodSugarQuota.text || "";
      const sugarDollarSigns = (resultSugarSubstitute.match(/\$/g) || []).length;
      if (sugarDollarSigns !== sugarValues.length) {
        console.warn(
          `تعداد $ (${sugarDollarSigns}) با تعداد آیتم‌ها (${sugarValues.length}) مطابقت ندارد`
        );
      }
      sugarValues.forEach((value) => {
        resultSugarSubstitute = resultSugarSubstitute.replace(/\$/, value);
      });

      return {
        text: resultSugarSubstitute,
        type: 'sugar'
      };
    }
  } catch (error) {
    console.error("خطا در تولید سهمیه قند/آجیل:", error);
    return {
      text: dietOrder === 'increase' ? 'سهمیه آجیل ندارید' : 'سهمیه قند ندارید',
      type: dietOrder === 'increase' ? 'nuts' : 'sugar'
    };
  }
};

/**
 * تولید سهمیه آجیل (تابع جداگانه مطابق myFavMenu.jsx)
 * @param {number} quotaCal3 - کالری سهمیه آجیل
 * @param {Array} formattedData - داده‌های فرمت شده منوها
 * @param {Object} usedMenus - منوهای استفاده شده (اختیاری)
 * @param {Object} selectedMenusHistory - تاریخچه منوهای انتخاب شده (اختیاری)
 * @param {string} currentDate - تاریخ فعلی (اختیاری)
 * @returns {Object|null} - object شامل text و menuId یا null
 */
export const generateNutsQuotaMenu = (quotaCal3, formattedData, usedMenus = null, selectedMenusHistory = null, currentDate = null) => {
  try {
    // بررسی پیش‌نیازها
    if (!quotaCal3 || quotaCal3 <= 0) {
      console.warn("مقدار quotaCal3 نامعتبر است:", quotaCal3);
      return null;
    }

    if (!formattedData || formattedData.length === 0) {
      console.warn("داده‌های سهمیه‌ها خالی هستند.");
      return null;
    }

    // فیلتر سهمیه‌های آجیل
    const nutsQuotas = formattedData.filter(
      (menu) => menu.form_type === "portion" && 
      menu.portion_type === "سهمیه آجیل" && 
      menu.Breakfast_Name === "مغز" && 
      menu.items.length > 0
    );

    if (nutsQuotas.length === 0) {
      console.error("هیچ سهمیه آجیل معتبری یافت نشد.");
      return null;
    }

    // انتخاب تصادفی یک سهمیه
    const nutsRandomIndex = Math.floor(Math.random() * nutsQuotas.length);
    const foodNutsQuota = nutsQuotas[nutsRandomIndex];

    // پردازش آیتم‌ها
    const nutsValues = foodNutsQuota.items.map((item) => {
      const percentage = item.percentage / 100;
      const calories = item.calories;
      const targetCalories = percentage * quotaCal3;
      
      let result;
      switch (item.rounding_method) {
        case "round_half":
          result = customRound(targetCalories / calories);
          break;
        case "round_5g":
          result = customRound2(targetCalories / calories);
          break;
        case "round_quarter":
          result = categorizeFraction(targetCalories / calories);
          break;
        case "round_full":
          result = customRoundFull(targetCalories / calories);
          break;
        default:
          result = categorizeFraction(targetCalories / calories);
      }
      
      // اطمینان از اینکه نتیجه حداقل 1 باشد (مگر اینکه targetCalories صفر باشد)
      // برای round_quarter نیازی به Math.max نیست چون نتیجه string است
      if (targetCalories > 0 && item.rounding_method !== "round_quarter" && item.rounding_method !== "default") {
        result = Math.max(result, 1);
      }

      return result;
    });

    // تولید متن سهمیه
    let resultNutsQuota = foodNutsQuota.text || "";
    const nutsDollarSigns = (resultNutsQuota.match(/\$/g) || []).length;
    if (nutsDollarSigns !== nutsValues.length) {
      console.warn(
        `تعداد $ (${nutsDollarSigns}) با تعداد آیتم‌ها (${nutsValues.length}) مطابقت ندارد`
      );
    }
    nutsValues.forEach((value) => {
      resultNutsQuota = resultNutsQuota.replace(/\$/, value);
    });

    return {
      text: resultNutsQuota,
      menuId: foodNutsQuota.id
    };

  } catch (error) {
    console.error("خطا در تولید سهمیه آجیل:", error);
    return null;
  }
};

/**
 * تولید سهمیه قند (تابع جداگانه مطابق myFavMenu.jsx)
 * @param {number} quotaCal3 - کالری سهمیه قند
 * @param {Array} formattedData - داده‌های فرمت شده منوها
 * @param {Object} usedMenus - منوهای استفاده شده (اختیاری)
 * @param {Object} selectedMenusHistory - تاریخچه منوهای انتخاب شده (اختیاری)
 * @param {string} currentDate - تاریخ فعلی (اختیاری)
 * @returns {Object|null} - object شامل text و menuId یا null
 */
export const generateSugarQuotaMenu = (quotaCal3, formattedData, usedMenus = null, selectedMenusHistory = null, currentDate = null) => {
  try {
    // بررسی پیش‌نیازها
    if (!quotaCal3 || quotaCal3 <= 0) {
      console.warn("مقدار quotaCal3 نامعتبر است:", quotaCal3);
      return null;
    }

    if (!formattedData || formattedData.length === 0) {
      console.warn("داده‌های سهمیه‌ها خالی هستند.");
      return null;
    }

    // فیلتر سهمیه‌های قند
    const sugarQuotas = formattedData.filter(
      (menu) => menu.form_type === "portion" && 
      menu.portion_type === "سهمیه قند" && 
      menu.Breakfast_Name === "حبه قند" && 
      menu.items.length > 0
    );

    if (sugarQuotas.length === 0) {
      console.error("هیچ سهمیه قند معتبری یافت نشد.");
      return null;
    }

    // انتخاب تصادفی یک سهمیه
    const sugarRandomIndex = Math.floor(Math.random() * sugarQuotas.length);
    const foodSugarQuota = sugarQuotas[sugarRandomIndex];

    // پردازش آیتم‌ها
    const sugarValues = foodSugarQuota.items.map((item) => {
      const percentage = item.percentage / 100;
      const calories = item.calories;
      const targetCalories = percentage * quotaCal3;
      
      let result;
      switch (item.rounding_method) {
        case "round_half":
          result = customRound(targetCalories / calories);
          break;
        case "round_5g":
          result = customRound2(targetCalories / calories);
          break;
        case "round_quarter":
          result = categorizeFraction(targetCalories / calories);
          break;
        case "round_full":
          result = customRoundFull(targetCalories / calories);
          break;
        default:
          result = Math.round(targetCalories / calories);
      }
      
      // اطمینان از اینکه نتیجه حداقل 1 باشد (مگر اینکه targetCalories صفر باشد)
      // برای round_quarter نیازی به Math.max نیست چون نتیجه string است
      if (targetCalories > 0 && item.rounding_method !== "round_quarter") {
        result = Math.max(result, 1);
      }

      return result;
    });

    // تولید متن سهمیه
    let resultSugarQuota = foodSugarQuota.text || "";
    const sugarDollarSigns = (resultSugarQuota.match(/\$/g) || []).length;
    if (sugarDollarSigns !== sugarValues.length) {
      console.warn(
        `تعداد $ (${sugarDollarSigns}) با تعداد آیتم‌ها (${sugarValues.length}) مطابقت ندارد`
      );
    }
    sugarValues.forEach((value) => {
      resultSugarQuota = resultSugarQuota.replace(/\$/, value);
    });

    return {
      text: resultSugarQuota,
      menuId: foodSugarQuota.id
    };

  } catch (error) {
    console.error("خطا در تولید سهمیه قند:", error);
    return null;
  }
};

